<script setup>
import { computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import DicaDoCampo from 'dashboard/components-next/captain/pageComponents/assistant/DicaDoCampo.vue';

// Aba "O que sabe" (fatia 4 das 5 abas, 02/09/2026): o que a assistente tem
// para responder. Perguntas aprovadas, as que ela aprendeu e esperam aprovação
// (em destaque, porque é o que mais melhora a resposta), situações, textos lidos
// e arquivos que pode enviar. Cada bloco resume e leva para a lista que já existe.
const props = defineProps({
  assistantId: { type: Number, required: true },
  assistant: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const pendentes = useMapGetter('captainResponses/getPendingCount');
const respostasMeta = useMapGetter('captainResponses/getMeta');
const cenarios = useMapGetter('captainScenarios/getRecords');
const documentos = useMapGetter('captainDocuments/getRecords');

const aprovadas = computed(() =>
  Number(
    respostasMeta.value?.totalCount ?? respostasMeta.value?.total_count ?? 0
  )
);
const materiais = computed(() => props.assistant?.config?.materials || []);

const rota = (name, query) => ({
  name,
  params: { accountId: route.params.accountId, assistantId: props.assistantId },
  ...(query ? { query } : {}),
});

onMounted(() => {
  const id = props.assistantId;
  store.dispatch('captainResponses/fetchPendingCount', id).catch(() => {});
  store
    .dispatch('captainResponses/get', {
      assistantId: id,
      status: 'approved',
      page: 1,
    })
    .catch(() => {});
  store.dispatch('captainScenarios/get', { assistantId: id }).catch(() => {});
  store.dispatch('captainDocuments/get', { assistantId: id }).catch(() => {});
});
</script>

<template>
  <div class="flex flex-col gap-6" data-testid="aba-o-que-sabe">
    <section
      class="flex flex-col gap-3 rounded-2xl border p-4"
      :class="pendentes > 0 ? 'border-n-amber-8/50' : 'border-n-weak'"
      data-testid="bloco-pendentes"
    >
      <h2 class="flex items-center gap-2 text-base font-medium text-n-slate-12">
        {{
          pendentes > 0
            ? t('CAPTAIN.O_QUE_SABE.PENDENTES.TITULO', { n: pendentes })
            : t('CAPTAIN.O_QUE_SABE.PENDENTES.NENHUMA')
        }}
        <DicaDoCampo termo="FAQ" />
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ t('CAPTAIN.O_QUE_SABE.PENDENTES.DESCRICAO') }}
      </p>
      <router-link
        v-if="pendentes > 0"
        :to="rota('captain_assistants_responses_pending')"
        class="text-sm font-medium text-n-brand"
        data-testid="ver-pendentes"
      >
        {{ t('CAPTAIN.O_QUE_SABE.PENDENTES.VER', { n: pendentes }) }}
      </router-link>
    </section>

    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <section
        class="flex flex-col gap-2 rounded-2xl border border-n-weak p-4"
        data-testid="bloco-faqs"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{ t('CAPTAIN.O_QUE_SABE.FAQS.TITULO', { n: aprovadas }) }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.O_QUE_SABE.FAQS.DESCRICAO') }}
        </p>
        <router-link
          :to="rota('captain_assistants_responses_index')"
          class="text-sm font-medium text-n-brand"
        >
          {{
            aprovadas > 0
              ? t('CAPTAIN.O_QUE_SABE.FAQS.VER')
              : t('CAPTAIN.O_QUE_SABE.FAQS.CRIAR')
          }}
        </router-link>
      </section>

      <section
        class="flex flex-col gap-2 rounded-2xl border border-n-weak p-4"
        data-testid="bloco-cenarios"
      >
        <h3
          class="flex items-center gap-2 text-base font-medium text-n-slate-12"
        >
          {{ t('CAPTAIN.O_QUE_SABE.CENARIOS.TITULO', { n: cenarios.length }) }}
          <DicaDoCampo termo="CENARIO" />
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.O_QUE_SABE.CENARIOS.DESCRICAO') }}
        </p>
        <ul
          v-if="cenarios.length"
          class="m-0 flex list-disc flex-col gap-1 pl-5 text-sm text-n-slate-12"
        >
          <li v-for="cenario in cenarios.slice(0, 3)" :key="cenario.id">
            {{ cenario.title }}
          </li>
        </ul>
        <router-link
          :to="rota('captain_assistants_scenarios_index')"
          class="text-sm font-medium text-n-brand"
        >
          {{
            cenarios.length
              ? t('CAPTAIN.O_QUE_SABE.CENARIOS.VER')
              : t('CAPTAIN.O_QUE_SABE.CENARIOS.CRIAR')
          }}
        </router-link>
      </section>

      <section
        class="flex flex-col gap-2 rounded-2xl border border-n-weak p-4"
        data-testid="bloco-documentos"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{
            t('CAPTAIN.O_QUE_SABE.DOCUMENTOS.TITULO', { n: documentos.length })
          }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.O_QUE_SABE.DOCUMENTOS.DESCRICAO') }}
        </p>
        <router-link
          :to="rota('captain_assistants_documents_index')"
          class="text-sm font-medium text-n-brand"
        >
          {{
            documentos.length
              ? t('CAPTAIN.O_QUE_SABE.DOCUMENTOS.VER')
              : t('CAPTAIN.O_QUE_SABE.DOCUMENTOS.CRIAR')
          }}
        </router-link>
      </section>

      <section
        class="flex flex-col gap-2 rounded-2xl border border-n-weak p-4"
        data-testid="bloco-materiais"
      >
        <h3 class="text-base font-medium text-n-slate-12">
          {{
            t('CAPTAIN.O_QUE_SABE.MATERIAIS.TITULO', { n: materiais.length })
          }}
        </h3>
        <p class="text-sm text-n-slate-11">
          {{ t('CAPTAIN.O_QUE_SABE.MATERIAIS.DESCRICAO') }}
        </p>
        <ul
          v-if="materiais.length"
          class="m-0 flex list-disc flex-col gap-1 pl-5 text-sm text-n-slate-12"
        >
          <li
            v-for="(material, i) in materiais.slice(0, 3)"
            :key="material.id || i"
          >
            {{ material.title || material.name || material.filename }}
          </li>
        </ul>
        <router-link
          :to="rota('captain_assistants_materials_index')"
          class="text-sm font-medium text-n-brand"
        >
          {{
            materiais.length
              ? t('CAPTAIN.O_QUE_SABE.MATERIAIS.VER')
              : t('CAPTAIN.O_QUE_SABE.MATERIAIS.CRIAR')
          }}
        </router-link>
      </section>
    </div>
  </div>
</template>
